import { Fragment, useEffect, useMemo, useRef } from 'react';
import {
  Badge,
  ColumnManager,
  FilterableTH,
  TBody,
  TD,
  THead,
  TR,
  Table,
  TableWrap,
  type FilterableTHProps,
  type FrozenSide,
  type SortDirection,
} from '@/components/ui';
import { formatList, pluralise } from '@/lib/format';
import { CompactFigure } from '@/components/dashboards/TargetTiles';
import { cx } from '@/lib/cx';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import type { Mover, RevenueCurrency, RevenueCycle } from '@/lib/use-revenue-analytics';
import {
  DIRECTION_META,
  compactMoney,
  cycleColumnKey,
  formatPercentChange,
  type CurrencySwitchHint,
  type DirectionSummary,
  type DirectionTone,
  type MoverRow,
  type MoverSection,
} from './top-movers';

/**
 * The movements themselves, grouped into the five outcomes.
 *
 * ⚠️ **FIVE SECTIONS RATHER THAN ONE RANKING, AND THAT IS THE FEATURE.** A
 * single ordered list can only rank on one figure, and four of the five
 * outcomes do not share one — `stopped` and `new` have no change and no
 * percentage at all. Ranked together they would be ordered by whatever a
 * fallback happened to score them at, which is how the largest lost account
 * ends up in the middle of the table.
 *
 * ── 🔴 EVERY FILTER AND EVERY SORT LIVES ON A COLUMN HEADER ────────────────
 *
 * Owner's instruction, 11 September 2026: this table filters and sorts exactly
 * as the companies list does. So the headers are `FilterableTH`, fed by
 * `useTableFilters` in the panel, and there is no ordering control anywhere
 * else on the tab.
 *
 * ⚠️ **A HEADER IS THE ONLY ROUTE INTO THE SORT STATE.** `useTableFilters`
 * hands `onSort` out only for a column whose spec declares a `sortKey`, so a
 * sort offered anywhere else would be a control that silently refuses every
 * click — no error, no request, no console line. That is not a hypothetical:
 * `/companies` shipped exactly that defect and it went unreported for a week.
 * Nothing here builds a header by hand; every one is `headerFor(key)`.
 *
 * ⚠️ **THE ORDERING STILL APPLIES WITHIN EACH SECTION, NEVER ACROSS THEM.**
 * Moving the control onto a column did not change what it ranks — see
 * `groupByDirection`. A reader sorting by `% change` is asking each outcome to
 * read in that order, because the five do not share a scale.
 *
 * ⚠️ **SO HIDING A COLUMN CAN TAKE THE SORT CONTROL AWAY WITH IT**, which the
 * column manager made reachable. The sort is moved to a visible column rather
 * than left pointing at a heading that is gone — see the strand note in
 * {@link TopMoversTable}, and `moverOrderFrom`, which rules the same way on the
 * other route into the same state.
 *
 * ── THE COLUMNS ARE THE READER'S, AND SOME OF THEM ARE DATA ────────────────
 *
 * ⚠️ **`columnKeys` IS A FUNCTION OF THE CYCLE SELECTION**, so this table's key
 * list grows and shrinks under the stored layout. `useColumnLayout` is built for
 * that — it drops a stored key the table no longer has and defaults a key the
 * table has GAINED to visible — but every consumer here has to tolerate it too:
 * `labelOf` never throws on an unknown key, and `MoverCell` answers one with an
 * empty cell rather than with nothing at all.
 */

/* -------------------------------------------------------------------------
 * Cells
 * --------------------------------------------------------------------- */

const FIGURE_TONE: Readonly<Record<DirectionTone, string>> = {
  success: 'text-success',
  info: 'text-info',
  warning: 'text-warning',
  danger: 'text-danger',
  neutral: 'text-fg-secondary',
};

/**
 * An amount, or an honest absence.
 *
 * 🔴 **`null` RENDERS AS AN EM DASH, NEVER AS `0.00`.** A company that took
 * nothing in this currency and a company that took exactly zero are different
 * facts, and the endpoint is careful to keep them apart — collapsing them here
 * would report a lost customer as one that broke even.
 *
 * ⚠️ **COMPACT SINCE 13 SEPTEMBER 2026, SO THE EXACT DECIMAL MOVES TO THE
 * `title`.** `£1.20M` has thrown away everything below ten thousand, and this is
 * a revenue figure somebody reconciles against an invoice — so the precise
 * number stays one hover away rather than being lost. The `null` branch above
 * keeps its own `title`, which says something different and must not be merged
 * with this one: an absence is not an imprecise figure.
 */
function Amount({
  value,
  currency,
  cycleName,
}: {
  value: string | null;
  currency: RevenueCurrency;
  cycleName: string;
}) {
  if (value === null) {
    return (
      <span
        className="text-fg-subtle"
        title={`Nothing in ${currency} in ${cycleName}. This is an absence, not a total of zero.`}
      >
        —
      </span>
    );
  }
  const figure = compactMoney(value, currency);
  return (
    <span className="tabular" title={figure.title}>
      <CompactFigure parts={figure.parts} />
    </span>
  );
}

/**
 * The change between the two cycles.
 *
 * ⚠️ **`null` ON `new` AND `stopped`, AND NOTHING MAY BE SUBSTITUTED.** There is
 * no difference to state when one side does not exist — the outcome badge is
 * what says what happened, and the title says why the cell is empty rather than
 * leaving a reader to wonder whether a figure failed to load.
 */
/*
 * ⚠️ **THE CURRENCY CODE DOES NOT TRAIL THE FIGURE.** It used to, on every row,
 * which put the same three letters on screen a hundred and twenty times to say
 * something the panel's own caption already says once — and the tab shows ONE
 * currency at a time by design, so it cannot vary between rows. What it cost
 * was width in the column where a long negative number needs it most.
 *
 * 🔴 **THE ONE SIGNED FIGURE ON THIS TAB, AND COMPACTING IT IS WHERE A MINUS
 * SIGN WOULD GO MISSING.** A dropped `-` turns a collapse into a gain, in the
 * column this whole panel exists to be read down, and nothing anywhere errors —
 * the figure is still plausible and still the right magnitude. `compactMoney`
 * strips the sign off before the shared formatter is handed anything, so
 * neither a swallowed sign nor a doubled one (`+-£340.00K`) is expressible.
 *
 * ⚠️ **THE `+` RULE IS UNCHANGED, AND IS PASSED IN RATHER THAN RE-DERIVED.** It
 * belongs to a rise and to nothing else, so a `flat` change of exactly `0.00`
 * still carries no sign at all.
 */
function ChangeCell({ mover, currency }: { mover: Mover; currency: RevenueCurrency }) {
  const meta = DIRECTION_META[mover.direction];
  if (mover.change === null) {
    return (
      <span
        className="text-fg-subtle"
        title={
          mover.direction === 'new'
            ? 'There is no earlier figure to subtract from, so there is no change to state.'
            : 'There is no later figure, so there is no change to state.'
        }
      >
        —
      </span>
    );
  }
  const positive = !mover.change.startsWith('-');
  const figure = compactMoney(mover.change, currency, positive && mover.direction === 'up');
  return (
    <span
      className={cx('tabular font-medium', FIGURE_TONE[meta.tone])}
      title={figure.title}
    >
      <CompactFigure parts={figure.parts} />
    </span>
  );
}

/**
 * 🔴 **THE PERCENTAGE COLUMN, WHERE MOST OF THE LYING WOULD HAPPEN.**
 *
 * Four of the five outcomes have no percentage:
 *
 * - `new` — growth from nothing is not *infinite%* and it is not *100%*. It is
 *   not a ratio at all, so the WORD is printed.
 * - `stopped` — the same in reverse. Printing `-100%` would file a lost
 *   customer next to one that merely halved.
 * - `up`/`down`/`flat` whose earlier cycle was exactly `0.00` — a real state,
 *   and dividing by it is an infinity. The direction still reads; the ratio
 *   does not exist.
 *
 * ⚠️ **THE WORDS ARE PRINTED RATHER THAN A BLANK**, even though the outcome
 * badge already carries them. A reader scanning this column alone must never
 * conclude the figure failed to load — an em dash in a percentage column reads
 * as missing data, and this one is not missing.
 */
function PercentCell({ mover }: { mover: Mover }) {
  const meta = DIRECTION_META[mover.direction];

  if (mover.direction === 'new' || mover.direction === 'stopped') {
    return (
      <span
        className={cx('text-xs font-medium', FIGURE_TONE[meta.tone])}
        title={
          mover.direction === 'new'
            ? 'No revenue in the earlier cycle, so this is not a percentage.'
            : 'No revenue in the later cycle, so this is not a percentage.'
        }
      >
        {meta.badge}
      </span>
    );
  }

  if (mover.percent === null) {
    return (
      <span
        className="text-fg-subtle"
        title="The earlier cycle was exactly 0.00, so a percentage change does not exist."
      >
        —
      </span>
    );
  }

  return (
    <span className={cx('tabular', FIGURE_TONE[meta.tone])}>
      {formatPercentChange(mover.percent)}
    </span>
  );
}

/**
 * 🔴 **THE CURRENCY-SWITCH CAVEAT, ON THE ROW IT QUALIFIES.**
 *
 * 🔴 **IT NEVER RENDERS ON A CONVERTED RESPONSE, AND THAT IS THE TRAP CLOSING
 * RATHER THAN THE WARNING BEING SUPPRESSED — 2026-09-18.**
 *
 * ⚠️ *SUPERSEDED, the premise: "This tab shows one currency at a time, by
 * decision … this tab was deliberately left filtering, so the trap below is
 * unchanged."* `GET /revenue-tracker` takes a `baseCurrency` now, so a company
 * that moved from dollars to euros has both summed into the base and is neither
 * `stopped` nor `new`. `currencySwitchHint` returns `null` because there is no
 * second currency on the row to find.
 *
 * **The trap, for the response where it is still live:** filtered, such a company
 * reads as `stopped` under one currency and `new` under the other — one ordinary
 * billing change producing two false findings, and *"stopped"* is the one
 * somebody picks up the phone about.
 *
 * ⚠️ **Such a row may not be presented as a lost or won customer without this
 * qualification**, which is why it sits on the row rather than in a footnote.
 * It is a WARNING and not a description, so the pass that stripped explanatory
 * prose from this tab deliberately left it alone. ⛔ **And it is not deleted on
 * the strength of not firing today**: it is the unconverted path's only mark, and
 * it returns of its own accord there because it follows the data rather than a
 * flag.
 */
function SwitchWarning({ hint }: { hint: CurrencySwitchHint }) {
  const currencies = formatList(hint.currencies);
  return (
    <span
      className="mt-0.5 inline-flex items-center gap-1 text-2xs font-medium text-warning"
      title={
        hint.direction === 'stopped'
          ? `This company was still being paid in ${currencies} in ${hint.cycleName}. It may have changed billing currency rather than stopped buying.`
          : `This company was already being paid in ${currencies} in ${hint.cycleName}. It may have changed billing currency rather than being newly won.`
      }
    >
      <span aria-hidden="true">⚠</span>
      {hint.direction === 'stopped'
        ? `Still billed in ${currencies} — may be a currency change, not a loss`
        : `Was billed in ${currencies} — may be a currency change, not a new account`}
    </span>
  );
}

/**
 * ⚠️ **THE BILLING REGION IS NO LONGER PRINTED UNDER THE COMPANY NAME.** It is
 * a COLUMN now, because that is where its funnel had to live — the region
 * picker used to sit in the filter bar, and the owner asked for column-level
 * filtering. Printing it in both places would be the same fact twice on one
 * row, which is exactly the "unnecessary data" this pass removed.
 */
function CompanyCell({ row, currency }: { row: MoverRow; currency: RevenueCurrency }) {
  const { mover, switchHint, otherCurrencies } = row;
  return (
    <div className="flex min-w-0 flex-col">
      <span className="font-medium break-words text-fg">{mover.row.company.name}</span>

      {switchHint !== null && <SwitchWarning hint={switchHint} />}

      {/*
       * A weaker signal than the hint above and a different one: this company
       * has been paid in more than one currency at some point, so the figures
       * on this row are not the whole of what it has paid us. Said quietly,
       * because it does not change what the outcome means.
       */}
      {/*
       * ⚠️ *SUPERSEDED 2026-09-14, the clause that ended this sentence:
       * ", because no exchange rate is stored anywhere in this system."*
       * `f_exchange_rates` exists (backend migration 178), so that was an
       * absence claim that rotted the moment somebody built the thing.
       *
       * 🔴 **AND THE WHOLE MARK IS UNREACHABLE ON A CONVERTED RESPONSE —
       * 2026-09-18.** `otherCurrencies` is empty because every line states the
       * base, so the figures on this row ARE the whole of what this company has
       * paid and a mark calling them partial would be false. The `title`'s
       * closing clause — *"This row shows GBP only"* — is the sentence that
       * would go wrong first if this ever rendered converted, and it cannot:
       * the array is what gates it.
       */}
      {switchHint === null && otherCurrencies.length > 0 && (
        <span
          className="text-2xs text-fg-muted"
          title={`Also billed in ${formatList(otherCurrencies)} at some point. This row shows ${currency} only.`}
        >
          Also billed in {formatList(otherCurrencies)}
        </span>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Sections
 * --------------------------------------------------------------------- */

const SECTION_TONE: Readonly<Record<DirectionTone, string>> = {
  success: 'text-success',
  info: 'text-info',
  warning: 'text-warning',
  danger: 'text-danger',
  neutral: 'text-fg-secondary',
};

/**
 * The band each section opens with, tinted in its own outcome's tone.
 *
 * 🔴 **THIS IS HOW `Stopped` STOPS BEING THE HARDEST THING TO REACH.** The
 * sections read in the owner's order — growing at the top, gone at the bottom —
 * which is right for reading and puts the one finding somebody would act on
 * today at the foot of five stacked tables. A uniform grey band gives a reader
 * scrolling past it nothing to aim at; a red one is findable at a glance and at
 * a distance, which is the whole of what a long analytical table needs.
 *
 * ⚠️ **The tint is a SECOND signal, never the only one.** The heading still
 * carries the word, so the band survives a reader who cannot separate the two
 * tones and survives a monochrome print.
 */
/*
 * ⚠️ 2026-10-11: the band keeps its outcome's coloured LEFT EDGE — the thing a
 * reader aims at — over a neutral sunken background. *SUPERSEDED: each band was
 * also washed in its outcome's soft fill*, which made five stacked tables read as
 * a row of tinted slabs. The edge plus the written label still carry the outcome.
 */
const SECTION_BAND: Readonly<Record<DirectionTone, string>> = {
  success: 'border-l-success-border bg-surface-sunken',
  info: 'border-l-info-border bg-surface-sunken',
  warning: 'border-l-warning-border bg-surface-sunken',
  danger: 'border-l-danger-border bg-surface-sunken',
  neutral: 'border-l-border bg-surface-sunken',
};

function SectionHeader({
  section,
  summary,
  currency,
  columnCount,
}: {
  section: MoverSection;
  summary: DirectionSummary | undefined;
  currency: RevenueCurrency;
  columnCount: number;
}) {
  const meta = DIRECTION_META[section.direction];
  /*
   * ⚠️ **`null` IS NOT ZERO**, so the whole clause is dropped rather than
   * rendering a band that claims a total of `0.00`. Resolved once here because
   * the figure and its `title` are the same derivation.
   */
  const band =
    summary !== undefined && summary.amount !== null
      ? compactMoney(summary.amount, currency)
      : null;
  return (
    <TR>
      <TD
        colSpan={columnCount}
        /*
         * ⚠️ **NO PADDING UTILITY HERE.** `TD` bakes in `px-3 py-2.5`, and a
         * conflicting utility passed through `className` resolves by STYLESHEET
         * order rather than by the order it appears in the attribute — so a
         * tighter band would win or lose depending on how Tailwind happened to
         * emit the two rules. The band takes the cell default and says so.
         */
        className={cx('border-l-4', SECTION_BAND[meta.tone])}
      >
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span
              className={cx('text-xs font-semibold', SECTION_TONE[meta.tone])}
            >
              {meta.label}
            </span>
            <span className="text-2xs text-fg-subtle">
              {pluralise(section.rows.length, 'company', 'companies')}
            </span>
          </span>

          {/*
            ⚠️ **The band's money is the SUMMARY's figure, which counts the WHOLE
            comparison rather than the rows beneath it.** That is deliberate
            upstream — the tiles are the tab's map of what exists — so the two can
            legitimately disagree while a filter is on, and the label says what
            the figure is a total OF rather than leaving it to be read as the
            section's own subtotal.

            ⚠️ **`null` is not zero here either**, so the whole clause is dropped
            rather than rendering `0.00 more than last cycle`.
          */}
          {band !== null && (
            <span className="text-2xs text-fg-muted">
              {/*
                🔴 **THE CODE IS REMOVED — owner's instruction, 2026-09-13 — AND
                THE COMMENT THAT DEFENDED IT WAS WRONG ABOUT ITS OWN OUTPUT.**

                *SUPERSEDED: "THE CODE STAYS BESIDE THE COMPACTED FIGURE HERE,
                AND IT IS NOT A REPETITION OF THE SYMBOL. This band totals a
                whole outcome, so it is the one money figure on the tab a reader
                is likely to quote out of context."*

                ⚠️ **It WAS a repetition, and that is measured rather than
                argued**: driven in a browser on 2026-09-13 this band rendered
                **`US$886.79K USD`** — `CompactFigure` emits the symbol, so the
                code beside it stated the unit twice. The claim was made about a
                sibling component that had no symbol and was never re-checked
                against what this one actually draws.

                ✅ **THE OTHER HALF OF THAT ARGUMENT SURVIVES AND IS UNTOUCHED
                BY THIS**: the `Shrinking` band's total is a SUM OF NEGATIVE
                CHANGES and carries a `-` that must survive the compaction, or
                the band reads as a gain. That is a property of `band.parts`,
                not of the code beside it.
              */}
              <span className="tabular font-medium text-fg-secondary" title={band.title}>
                <CompactFigure parts={band.parts} />
              </span>{' '}
              {meta.amountLabel}
            </span>
          )}
        </div>
      </TD>
    </TR>
  );
}

/* -------------------------------------------------------------------------
 * The table
 * --------------------------------------------------------------------- */

/* -------------------------------------------------------------------------
 * The reader's own column arrangement
 * --------------------------------------------------------------------- */

/**
 * The scope the stored layout lives under, reusing the saved-filter scope
 * names. It is a CONSTANT and not derived from the cycle selection: the
 * arrangement is a property of the table, and keying it per selection would
 * give a reader a different arrangement every time they ticked a cycle.
 */
const LAYOUT_SCOPE = 'top-movers';

/**
 * 🔴 **`company` IS LOCKED, AND IT ONLY BECAME LOCKABLE WHEN `ColumnManager`
 * GAINED `lockReasonOf`.**
 *
 * ⚠️ *SUPERSEDED: "**NOTHING IS LOCKED, AND THAT IS A FACT ABOUT
 * `ColumnManager` RATHER THAN A VIEW ABOUT THIS TABLE** … the manager renders
 * ONE sentence for a locked row — "This column holds the row actions and cannot
 * be hidden" — and this table has no actions lane, so locking any column here
 * would put a flatly false sentence on screen … Locking becomes available here
 * the day `ColumnManager` takes a reason per key; until then the honest answer
 * is an empty set."* **That day arrived**, so the empty set is no longer the
 * honest answer — it was a workaround for a missing prop, and
 * {@link lockReasonOf} is the sentence that replaces it.
 *
 * ⚠️ **The reason has to be TRUE OF THIS TABLE, which is the whole point of the
 * prop.** It is not the actions-lane sentence: there is no actions lane. Every
 * other column here is a figure or a badge, so without the company name the
 * table is a grid of money attributable to nobody — a narrower view of nothing
 * rather than a narrower view of the data.
 *
 * ⚠️ **LOCKED IS NOT PINNED, and this column is exactly why the two are
 * separate arguments.** Locked means it cannot be hidden or reordered; it says
 * nothing about which edge it sits at, and `useColumnLayout` guards freezing on
 * `pinnedKeys` alone — so `company` stays the freeze-left candidate a reader
 * most wants, which is the case the feature exists for.
 */
const LOCKED_KEYS: readonly string[] = ['company'];

/**
 * Why `company` cannot be hidden, in the reader's terms.
 *
 * ⚠️ **A why-this-is-disabled sentence, which the no-helper-text rule keeps by
 * name** — a bare `disabled` switch makes a locked column and a broken control
 * identical. It is answered per key rather than returned unconditionally so a
 * second lock added here cannot inherit this one's reason.
 */
function lockReasonOf(key: string): string | undefined {
  return key === 'company'
    ? 'Every other column is a figure. Without the company there is nothing on the row to say whose money it is.'
    : undefined;
}

/**
 * The width utility on a header cell, where it has one.
 *
 * ⚠️ **This and {@link columnMinWidth} are ONE decision written twice**, and
 * they have to be read together: the class is what the column ASKS for and the
 * number is what the table refuses to squash below. The cycle columns carry no
 * class at all — they are as many as the reader selected — so the floor is the
 * only thing holding them open.
 */
const COLUMN_CLASS: Readonly<Record<string, string>> = {
  company: 'w-72',
  region: 'w-44',
  outcome: 'w-32',
};

/**
 * How wide each column refuses to squash below.
 *
 * ⚠️ *SUPERSEDED: a `tableMinWidth(cycleColumns)` LADDER of seven literal
 * `min-w-[…]` classes, 1040px through 1880px.* ⚠️ **The old values are spelled
 * without their utility prefix ON PURPOSE — Tailwind scans this source, so a
 * class quoted whole in a comment is EMITTED**, and a superseded ladder would
 * go on shipping seven dead rules that nobody would ever think to look for. It
 * was written out because
 * Tailwind scans source for literals and a computed `min-w-[${n}px]` emits no
 * rule at all — the table would silently lose its floor. The ladder could
 * therefore only count CYCLES, so it kept its full 1040px floor for three
 * columns the reader had hidden, and the floor it reserved for a hidden cycle
 * column was simply lost width.
 *
 * ⚠️ **The number now reaches the DOM through `style`, which is the same
 * escape `TH`'s frozen offset takes and for the identical reason.** ⚠️
 * *SUPERSEDED: "A `<div>` inside `TableWrap` carries it, because `Table`
 * accepts only `className`."* **`Table` takes a `style` now**, so it carries
 * its own floor and the spacer `<div>` is gone.
 *
 * ⚠️ **THE SUM IS UNCHANGED ON THE DEFAULT SELECTION — 288 + 176 + 128 + 140×2
 * + 84 + 84 = 1040**, byte for byte the ladder's two-cycle floor, so no reader
 * who has never opened the manager sees this table move. The per-cycle 140 is
 * the ladder's own increment.
 *
 * ⚠️ **The ladder's CAP at eight cycles is gone deliberately.** It existed
 * because a literal ladder cannot be infinite, not because a ninth cycle should
 * be squashed — the comment that carried it said so ("past eight the floor
 * stops growing and `TableWrap` scrolls, which it does anyway"). A computed sum
 * has no such limit, so every cycle column now keeps its 140px and the wrapper
 * scrolls, which is what it was doing regardless.
 */
const COLUMN_MIN_WIDTH: Readonly<Record<string, number>> = {
  company: 288,
  region: 176,
  outcome: 128,
  change: 84,
  percent: 84,
};

/** Every cycle column, however many the reader has selected. */
const CYCLE_COLUMN_MIN_WIDTH = 140;

function columnMinWidth(key: string): number {
  return COLUMN_MIN_WIDTH[key] ?? CYCLE_COLUMN_MIN_WIDTH;
}

/**
 * Which direction a column opens on when the sort has to move to it.
 *
 * Everything here is a figure and reads biggest-first; the two text columns
 * read A–Z, because a company list opening at Z is a ranking of nothing.
 */
const SORT_OPENS_ASCENDING: ReadonlySet<string> = new Set(['company', 'region']);

/*
 * ⚠️ *SUPERSEDED: a local `HeadCell` that re-implemented `FilterableTH` — cell,
 * funnel, label truncation and `aria-sort` — for the frozen case alone,* because
 * `FilterableTHProps` carried no `frozen` / `frozenOffset` and the offset cannot
 * be smuggled through `className`: it is a measured runtime number and a
 * computed Tailwind class emits no rule. **The primitive forwards both props
 * now, so the copy is deleted rather than kept** — it was a second
 * implementation of a header cell that nothing would have kept in step, and it
 * claimed `aria-sort` by re-deriving what the primitive already derives.
 */

/**
 * ⚠️ **AN UNFROZEN CELL MUST CARRY NEITHER PROP**, not `frozen={undefined}`
 * with an offset beside it. `TD` reads `frozen` alone to decide whether the
 * cell is positioned at all, and a stray `frozenOffset` on an unfrozen cell is
 * harmless only until somebody reads the two as a pair.
 */
function frozenPropsFor(
  key: string,
  frozenSideOf: (key: string) => FrozenSide | undefined,
  offsets: Record<string, number>,
): { frozen?: FrozenSide; frozenOffset?: number } {
  const side = frozenSideOf(key);
  if (side === undefined) return {};
  return { frozen: side, frozenOffset: offsets[key] ?? 0 };
}

/**
 * One body cell, chosen by column key.
 *
 * 🔴 **A CYCLE KEY IS TESTED FIRST, AND BY LOOKUP RATHER THAN BY SHAPE.** The
 * cycle columns are the reader's own selection, so the set of keys this has to
 * answer changes under it — `cycleByKey` is built from `cycleColumnKey`, the
 * same function the panel's `ColumnSpec`s and this table's headers use, which
 * is what stops a cell and its heading ever describing different cycles. A
 * `key.startsWith('cycle:')` test would look equivalent and would answer for a
 * cycle that is no longer selected.
 *
 * ⚠️ **AN UNRECOGNISED KEY RENDERS AN EMPTY CELL RATHER THAN NOTHING.** It
 * cannot happen from `moverColumnKeys`, but it must not be able to drop a cell
 * either: `useFrozenOffsets` indexes the header row by position, and a body row
 * one cell short shifts every column right of it — which no build, type or
 * console can see. Same degradation `headerFor` makes for an unknown key.
 */
function MoverCell({
  columnKey,
  row,
  currency,
  cycle,
  frozen,
  frozenOffset,
}: {
  columnKey: string;
  row: MoverRow;
  currency: RevenueCurrency;
  cycle: RevenueCycle | undefined;
  frozen?: FrozenSide;
  frozenOffset?: number;
}) {
  const frozenProps = { frozen, frozenOffset };

  /*
   * ⚠️ **EVERY cycle cell comes from `row.amounts`, the compared two
   * included.** The pair is also on `mover`, and reading it there for two
   * columns while reading `amounts` for the rest would be two routes to one
   * figure — they agree today because both are the same `revenueFor` call,
   * which is exactly the kind of agreement that holds until somebody changes
   * one of them.
   *
   * ⚠️ **`cycleName` is the cycle's OWN name**, not the column's label. The
   * label is what the column is headed with; the title on an em dash is a claim
   * about a cycle, and routing it through the header would make it depend on a
   * lookup that answers the raw key for anything unrecognised.
   */
  if (cycle !== undefined) {
    return (
      <TD numeric verticalAlign="top" {...frozenProps}>
        <Amount
          value={row.amounts[cycle.name] ?? null}
          currency={currency}
          cycleName={cycle.name}
        />
      </TD>
    );
  }

  switch (columnKey) {
    case 'company':
      return (
        <TD verticalAlign="top" {...frozenProps}>
          <CompanyCell row={row} currency={currency} />
        </TD>
      );

    case 'region':
      return (
        <TD verticalAlign="top" {...frozenProps}>
          {/*
           * ⚠️ An unrecorded billing region is ORDINARY, not a fault —
           * `d_companies.country_id` is nullable and was never backfilled. It is
           * NAMED rather than left blank, because a blank cell reads as a
           * rendering bug and sends the reader hunting a defect instead of a
           * country. The funnel's own *Is empty* is how you filter to these.
           */}
          {row.mover.row.billingRegion === null ? (
            <span className="text-2xs text-fg-subtle">No billing region recorded</span>
          ) : (
            <span className="text-xs text-fg-secondary">{row.mover.row.billingRegion.name}</span>
          )}
        </TD>
      );

    case 'outcome':
      return (
        <TD verticalAlign="top" {...frozenProps}>
          <Badge tone={DIRECTION_META[row.mover.direction].tone} dot>
            {DIRECTION_META[row.mover.direction].badge}
          </Badge>
        </TD>
      );

    case 'change':
      return (
        <TD numeric verticalAlign="top" {...frozenProps}>
          <ChangeCell mover={row.mover} currency={currency} />
        </TD>
      );

    case 'percent':
      return (
        <TD numeric verticalAlign="top" {...frozenProps}>
          <PercentCell mover={row.mover} />
        </TD>
      );

    default:
      return <TD verticalAlign="top" {...frozenProps} />;
  }
}

export interface TopMoversTableProps {
  sections: readonly MoverSection[];
  summaries: readonly DirectionSummary[];
  currency: RevenueCurrency;
  /**
   * The cycle COLUMNS, oldest first — the reader's own selection.
   *
   * 🔴 **THE SAME ARRAY THE PANEL BUILT ITS `ColumnSpec`s FROM, PASSED RATHER
   * THAN RECOMPUTED.** `headerFor` answers an unknown key with a plain header
   * (`{ label: key }`) — no funnel, no sort, no error — so a table deriving its
   * own cycle list would render `cycle:11` as a heading the day the two
   * derivations disagreed, and nothing would fail. One array, two readers.
   *
   * ⚠️ **The last two are the comparison pair**; every earlier one is context.
   * See {@link MoverSort} for why only those two carry a sort control.
   */
  cycles: readonly RevenueCycle[];
  /**
   * Every column key in DECLARED table order. From `moverColumnKeys`, so it
   * carries one key per selected cycle.
   *
   * 🔴 **IT IS THE COLUMN MANAGER'S KEY LIST AS WELL AS THE RENDER ORDER NOW**,
   * and both the header and the body are drawn from it rather than written out.
   * ⚠️ *SUPERSEDED: "for `colSpan` on the section bands and the empty row"* —
   * those spans count the VISIBLE keys, which is a different number the moment
   * a reader hides anything.
   *
   * ⚠️ **DECLARED order, not rendered order.** `useColumnLayout` treats this as
   * the authority on which columns EXIST and the stored layout as the authority
   * on their order — so a column added here lands in this position on every
   * reader's table rather than at the end.
   */
  columnKeys: readonly string[];
  /** From `useTableFilters` — the same function the mobile filter sheet spreads. */
  headerFor: (key: string) => FilterableTHProps;
  /**
   * Rendered as a single row when `sections` is empty.
   *
   * 🔴 **THIS IS WHY THE TABLE STAYS MOUNTED WITH NOTHING IN IT, AND IT IS A
   * TRAP FOUND BY DRIVING IT.** Replacing the whole table with an `EmptyState`
   * when a filter matched nothing destroys the column headers — and since the
   * filters now LIVE in those headers, it destroys the only control able to
   * undo the filter, while the popover the reader is still looking at is
   * portalled off a header that no longer exists. Ticking *Is empty* on Billing
   * region did exactly that: the box would not even stay ticked, because the
   * element was unmounted mid-click.
   *
   * Same shape as the cycle picker that used to be able to remove itself from
   * the page, and as the Outcome column that used to vanish when you narrowed
   * to one outcome. **A control must survive the state it can put the screen
   * into.**
   */
  emptyMessage?: string;
}

export function TopMoversTable({
  sections,
  summaries,
  currency,
  cycles,
  columnKeys,
  headerFor,
  emptyMessage,
}: TopMoversTableProps) {
  /*
   * 🔴 **THE OUTCOME COLUMN IS UNCONDITIONAL NOW, AND THE CONDITIONAL IT
   * REPLACED WAS A TRAP THE MOMENT THE FUNNEL MOVED ONTO IT.**
   *
   * It used to render only while more than one section was on screen, on the
   * sound argument that a badge repeating the band above it is the same fact
   * twice. But the OUTCOME FILTER now lives in this column's header — so
   * narrowing to `Stopped` would have left exactly one section, taken the
   * column away, and with it the only header control able to undo the filter.
   * A reader would be stuck in a state whose control had disappeared, which is
   * the same shape as the cycle picker that could remove itself from the page.
   *
   * The duplication it costs is real and small; the argument for the column
   * across several outcomes is unchanged — a band scrolls off the top of a long
   * section, and by row thirty the badge is the only thing on the row saying
   * which of the five a company is in.
   *
   * ⚠️ **"UNCONDITIONAL" MEANS THE TABLE NEVER DECIDES, NOT THAT THE COLUMN
   * CANNOT BE HIDDEN.** The reader can hide it from the column manager, and that
   * is deliberately allowed: the trap above was the TABLE removing a control
   * behind somebody's back as a side effect of filtering, where this is the
   * reader's own explicit act. It is also not a dead end from any direction —
   * the outcome filter is still reachable from the summary tiles, which read and
   * write that same funnel state, from the panel's *Clear filters*, and from the
   * manager itself, which is not a table header and so does not vanish with the
   * column. **A control must survive the state it can put the screen into**, and
   * all three of those do.
   */
  /*
   * The reader's own arrangement of this table — order, hidden set and the two
   * frozen edges — read from the account rather than this browser.
   */
  const layout = useColumnLayout(LAYOUT_SCOPE, columnKeys, LOCKED_KEYS);
  const { visibleKeys, frozenSideOf } = layout;

  /*
   * ⚠️ **THE OFFSETS ARE MEASURED, AND THERE IS NOWHERE ELSE TO GET THEM.**
   * This table is auto-layout with no `<colgroup>`, so a `w-72` on a header is
   * a request the content can outvote — how far a frozen column actually sits
   * from the edge exists only in the DOM.
   */
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const frozenOffsets = useFrozenOffsets(headRowRef, visibleKeys, frozenSideOf);

  /*
   * 🔴 **ONE `headerFor` CALL PER COLUMN KEY, HIDDEN COLUMNS INCLUDED.** The
   * headers a reader can see are the obvious half; the stranded-sort test
   * below has to ask about a column this table is NOT drawing, and that is the
   * only place the active sort can hide.
   *
   * ⚠️ **The width utility is merged in HERE rather than at the call site**,
   * because a column is now rendered by key from a single list and there is no
   * longer a per-column JSX line to hang a `className` on. `headerFor` never
   * returns one, so nothing is being overwritten.
   */
  const headers = new Map<string, FilterableTHProps>(
    // Annotated as a TUPLE, or `map` infers `(string | FilterableTHProps)[]`
    // and the `Map` constructor refuses it.
    columnKeys.map((key): [string, FilterableTHProps] => {
      const header = headerFor(key);
      const className = COLUMN_CLASS[key];
      return [key, className === undefined ? header : { ...header, className }];
    }),
  );

  /**
   * ⚠️ **TOTAL, AND IT NEVER THROWS.** The manager lists every live key, cycle
   * columns included, and those come and go as the reader ticks cycles — so
   * this is asked about keys that did not exist a moment ago. `headerFor`
   * answers an unrecognised key with `{ label: key }` rather than an error, and
   * the final fallback to the raw key covers a key that is not in the map at
   * all. A manager row reading `cycle:11` would be ugly; one that threw would
   * take the dashboard down.
   */
  const labelOf = (key: string) => headers.get(key)?.label ?? key;

  /*
   * ⚠️ **KEYED THROUGH `cycleColumnKey`, the SAME function the panel's
   * `ColumnSpec` used.** The body cells are drawn from a key list now, so this
   * is what turns a key back into the cycle whose amount the cell prints.
   * Spelling the key by hand on either side is how a column comes to render a
   * raw key with no funnel under it.
   */
  const cycleByKey = useMemo(
    () =>
      new Map<string, RevenueCycle>(
        cycles.map((cycle): [string, RevenueCycle] => [cycleColumnKey(cycle), cycle]),
      ),
    [cycles],
  );

  /*
   * 🔴 **THE LIVE VISIBLE COUNT, NEVER `columnKeys.length`.** The section bands
   * and the empty row are `colSpan`ned across it, and a span wider than the
   * row draws a band that runs past the last column while a narrower one stops
   * short of it. Neither errors, and both look like a styling accident rather
   * than a count.
   */
  const columnCount = visibleKeys.length;

  const minWidth = visibleKeys.reduce((total, key) => total + columnMinWidth(key), 0);

  /* ── The sort must not be left on a column the reader cannot see ────────── */

  /*
   * 🔴 **A HIDDEN COLUMN CAN STRAND THE SORT, AND THE FILE HEADER SAYS WHY
   * THAT MATTERS: A HEADER IS THE ONLY ROUTE INTO THE SORT STATE.** Sort by
   * `% change`, hide `% change`, and the table stays ordered by a figure that
   * is no longer on screen with no caret anywhere to say so — and no control
   * left able to flip its direction or take it back.
   *
   * ⚠️ **`moverOrderFrom` ALREADY RULES ON THE IDENTICAL SITUATION** for the
   * other route into it — deselecting the cycle being sorted by — and its words
   * are the ones followed here: *"Ranking by a cycle whose column has been
   * removed orders the table by a number the reader can no longer see, which is
   * the same lie in a quieter place."* That path falls back to the default
   * ranking. This one cannot: the ordering happens in the panel, and the only
   * lever this table holds over the sort state is a header's own `onSort`.
   *
   * So the sort MOVES to a visible column, preferring `change` — whose
   * `movement` key sorted descending IS `DEFAULT_MOVER_ORDER`, so the common
   * case lands exactly where the module says it should. The caret appears on a
   * heading the reader can see, the row order changes in the same gesture that
   * hid the column, and nothing is left ordered by a number that is gone.
   *
   * ⚠️ **Only reachable by hiding a column that is CURRENTLY sorted**, because
   * a sort can only be set from a visible header in the first place, and
   * `useTableFilters` starts at `null`. There is no first-paint flicker.
   *
   * ⚠️ **The deselect route is NOT handled here and must not be.** Deselecting
   * a cycle removes the key from `columnKeys` entirely, so no header reports a
   * direction, `activeSortKey` is `null`, and `moverOrderFrom` does its own
   * documented fallback upstream. Two guards over one state would fight.
   */
  const activeSortKey =
    columnKeys.find((key) => (headers.get(key)?.filter?.sort?.direction ?? null) !== null) ?? null;

  const sortRefuge =
    activeSortKey === null || visibleKeys.includes(activeSortKey)
      ? null
      : visibleKeys.includes('change') && headers.get('change')?.filter?.sort !== undefined
        ? 'change'
        : (visibleKeys.find((key) => headers.get(key)?.filter?.sort !== undefined) ?? null);

  /*
   * ⚠️ **THE EFFECT KEYS ON THE REFUGE KEY ALONE — a string — and `headers` is
   * deliberately not a dependency.** `headerFor` is rebuilt every render
   * (`useTableFilters` says so in terms), so a fresh `Map` is too, and listing
   * it would run this on every render. Closing over it is the correct read
   * anyway: the effect fires on the render that DETECTED the strand, so the
   * `onSort` it reaches is the one that render drew the headers from.
   */
  useEffect(() => {
    if (sortRefuge === null) return;
    const sort = headers.get(sortRefuge)?.filter?.sort;
    if (sort === undefined) return;
    const direction: SortDirection = SORT_OPENS_ASCENDING.has(sortRefuge) ? 'asc' : 'desc';
    sort.onSort(direction);
    /*
     * ⚠️ **This cannot loop.** The refuge is a VISIBLE key by construction, so
     * the moment the sort lands on it `sortRefuge` is `null` and the effect's
     * next run returns immediately. It also does nothing when no visible column
     * declares a `sortKey` at all — there is no control to move the sort to,
     * and inventing one would be the dead sort control this file exists to
     * refuse.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortRefuge]);

  return (
    <>
      {/*
       * ⚠️ **THE TRIGGER LIVES HERE RATHER THAN IN THE PANEL'S `PanelHeader`
       * `actions` SLOT**, which is where `FilterSheet` and `Clear filters` sit.
       * That slot is `shrink-0` and cannot wrap, so a third control in it is
       * the overflow this repo has already paid for once — and the manager
       * belongs to the table it configures, which is rendered here.
       *
       * ⚠️ **WITHHELD UNTIL THE STORED LAYOUT HAS ARRIVED, and that is not
       * cosmetic.** Until the read resolves the controller holds the DEFAULT
       * layout, so a toggle pressed in that window would persist a hidden set
       * derived from nothing and overwrite the arrangement still in flight — a
       * `200`, no error, and a column the reader had hidden quietly back.
       *
       * ⚠️ **IT IS NOT GATED ON THE ROWS.** It renders over the filtered-empty
       * table too, because unhiding a column is the only way back for somebody
       * who hid one and then filtered the list to nothing.
       */}
      {!layout.loading && (
        <div className="flex items-center justify-end border-b border-border px-4 py-2">
          <ColumnManager
            layout={layout}
            labelOf={labelOf}
            lockedKeys={LOCKED_KEYS}
            lockReasonOf={lockReasonOf}
          />
        </div>
      )}

      {/* The tab's main table: header row pinned while it scrolls (owner 2026-10-18). */}
      <TableWrap frozenHeader>
        {/*
         * ⚠️ **THE FLOOR IS A STYLE, NOT A CLASS.** It is summed from the
         * columns actually on screen, and a computed `min-w-[${n}px]` is a
         * class Tailwind never scans and therefore never emits — the table
         * would silently lose its floor and squash every column.
         *
         * ⚠️ *SUPERSEDED: the number rode on a spacer `<div>` inside
         * `TableWrap`, "because `Table` takes only `className`".* **`Table`
         * takes a `style` now**, so the floor sits on the element it describes.
         * The rendered result is unchanged: `Table` is `w-full`, so it fills
         * the scroller and still grows past this floor when content demands,
         * which is exactly what the literal ladder did.
         */}
        <Table style={{ minWidth }}>
          <THead>
            {/*
             * 🔴 **THE HOOK INDEXES THIS ROW'S CHILDREN BY POSITION IN
             * `visibleKeys`**, so this row must render exactly one cell per
             * visible key, in that order, and nothing else. A stray cell moves
             * every frozen offset outboard of it and the columns overlap by the
             * difference, silently.
             *
             * ⚠️ *SUPERSEDED: a hand-rolled `<tr>` carrying a copy of `TR`'s
             * classes, "because `TR` is not a `forwardRef`".* **`TR` takes a
             * `ref` now**, so the copy is gone and this row inherits whatever
             * `TR` emits rather than a snapshot of it.
             */}
            <TR ref={headRowRef}>
              {/*
               * ⚠️ **Every header is still `headerFor(key)` — none is written
               * by hand.** That is what makes a sort control impossible to
               * offer unless a column spec actually declares the `sortKey`
               * behind it, and it is what lets the cycle columns take their
               * LABEL from the spec so the headings name the years rather than
               * "before" and "after".
               *
               * ⚠️ **Only the comparison pair carries a sort control**, and
               * nothing here decides that — the panel gives a `sortKey` to
               * those two alone, and `ColumnFilter` draws no sort section
               * without one. See `MoverSort` for the argument.
               */}
              {visibleKeys.map((key) => (
                <FilterableTH
                  key={key}
                  {...(headers.get(key) ?? { label: key })}
                  {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
                />
              ))}
            </TR>
          </THead>

          <TBody>
            {/*
             * ⚠️ **Both spans below are the LIVE visible count.** A band or an
             * empty row spanning the DECLARED count runs past the last column
             * the moment anything is hidden — nothing errors, and it reads as a
             * styling accident rather than as a count that is out of date.
             *
             * ⚠️ **Neither row is frozen, and neither can be.** A single cell
             * spanning every column has no edge to stick to, so a band scrolls
             * sideways under a frozen column while the rows beside it do not.
             * That is ordinary frozen-pane behaviour and is why the band
             * repeats its outcome in the badge on every row.
             */}
            {sections.length === 0 && emptyMessage !== undefined && (
              <TR>
                <TD colSpan={columnCount} className="text-center text-xs text-fg-muted">
                  {emptyMessage}
                </TD>
              </TR>
            )}
            {sections.map((section) => (
              <Fragment key={section.direction}>
                <SectionHeader
                  section={section}
                  summary={summaries.find((entry) => entry.direction === section.direction)}
                  currency={currency}
                  columnCount={columnCount}
                />
                {section.rows.map((row) => (
                  /*
                   * 🔴 **ONE CELL PER VISIBLE KEY, IN `visibleKeys` ORDER, AND
                   * NEVER FEWER.** `useFrozenOffsets` measures the header row
                   * and applies the result BY POSITION, so a body row that
                   * skips a cell shifts every column right of it against its
                   * heading. `MoverCell` answers an unknown key with an empty
                   * cell for exactly that reason.
                   */
                  <TR key={row.key}>
                    {visibleKeys.map((key) => (
                      <MoverCell
                        key={key}
                        columnKey={key}
                        row={row}
                        currency={currency}
                        cycle={cycleByKey.get(key)}
                        {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
                      />
                    ))}
                  </TR>
                ))}
              </Fragment>
            ))}
          </TBody>
        </Table>
      </TableWrap>
    </>
  );
}
